import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import type { Faculty, Subject, Lesson, LessonStateCount, WilayaItem, Language, Theme, Department } from '../lib/types';
import { loadInitialMetadata } from '../lib/supabase';
import { departmentOrder, getInitialLanguage } from '../lib/i18n';
import { getSavedTheme, saveTheme, saveLanguage } from '../lib/storage';

interface DataContextType {
  faculties: Faculty[];
  subjects: Subject[];
  lessons: Lesson[];
  counts: LessonStateCount[];
  wilayas: WilayaItem[];
  loading: boolean;
  error: string | null;
  retryLoad: () => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  getLessonCount: (lessonId: string, wilaya: string) => number;
  getSubjectCount: (subjectId: string, wilaya: string) => number;
  getFacultyCount: (facultyId: string, wilaya: string) => number;
  getYearsForFaculty: (faculty: Faculty) => Department[];
}

const DataContext = createContext<DataContextType | null>(null);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [counts, setCounts] = useState<LessonStateCount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [language, setLanguageState] = useState<Language>(getInitialLanguage);
  const [theme, setThemeState] = useState<Theme>(getSavedTheme);

  // Sync HTML lang and dir
  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    saveLanguage(lang);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    saveTheme(newTheme);
  }, []);

  // Initialize document attributes
  useEffect(() => {
    saveLanguage(language);
    saveTheme(theme);
  }, [language, theme]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await loadInitialMetadata();
      setFaculties(data.faculties);
      setSubjects(data.subjects);
      setLessons(data.lessons);
      setCounts(data.counts);
    } catch (err) {
      console.error('Failed to load metadata:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived Wilayas list
  const wilayas = useMemo<WilayaItem[]>(() => {
    const wilayaMap = new Map<string, number>();
    let totalAll = 0;

    for (const c of counts) {
      const stateName = (c.state || '').trim();
      if (!stateName) continue;
      const current = wilayaMap.get(stateName) || 0;
      wilayaMap.set(stateName, current + c.n);
      totalAll += c.n;
    }

    const sortedStates = Array.from(wilayaMap.entries())
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => a.state.localeCompare(b.state));

    return [
      { state: 'all', count: totalAll },
      ...sortedStates,
    ];
  }, [counts]);

  // Question count per lesson
  const getLessonCount = useCallback(
    (lessonId: string, wilaya: string) => {
      let sum = 0;
      for (const row of counts) {
        if (row.lesson_id === lessonId) {
          if (!wilaya || wilaya === 'all' || row.state === wilaya) {
            sum += row.n;
          }
        }
      }
      return sum;
    },
    [counts]
  );

  // Question count per subject
  const getSubjectCount = useCallback(
    (subjectId: string, wilaya: string) => {
      const subjectLessons = lessons.filter((l) => l.subject_id === subjectId);
      let total = 0;
      for (const lesson of subjectLessons) {
        total += getLessonCount(lesson.id, wilaya);
      }
      return total;
    },
    [lessons, getLessonCount]
  );

  // Question count per faculty
  const getFacultyCount = useCallback(
    (facultyId: string, wilaya: string) => {
      const facultySubjects = subjects.filter((s) => s.faculty_id === facultyId);
      let total = 0;
      for (const sub of facultySubjects) {
        total += getSubjectCount(sub.id, wilaya);
      }
      return total;
    },
    [subjects, getSubjectCount]
  );

  const getYearsForFaculty = useCallback((faculty: Faculty) => {
    const count = Math.min(Math.max(faculty.years_count || 6, 1), 6);
    return departmentOrder.slice(0, count);
  }, []);

  const value = useMemo(
    () => ({
      faculties,
      subjects,
      lessons,
      counts,
      wilayas,
      loading,
      error,
      retryLoad: loadData,
      language,
      setLanguage,
      theme,
      setTheme,
      getLessonCount,
      getSubjectCount,
      getFacultyCount,
      getYearsForFaculty,
    }),
    [
      faculties,
      subjects,
      lessons,
      counts,
      wilayas,
      loading,
      error,
      loadData,
      language,
      setLanguage,
      theme,
      setTheme,
      getLessonCount,
      getSubjectCount,
      getFacultyCount,
      getYearsForFaculty,
    ]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
